import { get, size } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { Creators as NotificationsCreators } from "modules/notifications/model"
import { Creators as PortfolioCreators } from "modules/portfolio-root/model"
import { classificationsQuery, generateClassificationQuery } from "helpers/classification"
import { getEndDate } from "selectors/dates"
import { getIsOpPro, getAiTermsAccepted } from "modules/account-root/selectors"
import { getEarliestDate, getLatestDate } from "modules/portfolio-root/selectors/dates"
import { getSelectedModelId, getViewType, getIsExpanded, getPortfolioName, getCurrency, getPeriod } from "modules/portfolio-root/selectors/portfolios"
import { getIsHasUnexpandedCompositesLoading } from "selectors/data"
import { getIsPortfolioFiltersLoading } from "selectors/portfolios"
import { getAvailableRollups, getGrouping, getIncludeNotHeld, getIsActiveView, getIsBenchmarkUnfiltered, getQuerySegment, getSegmentAwareBaseSignature, getShowNotHeld, getHasUnexpandedComposites, getHasUnsupportedContributionFilter, getBasePositionSetWithSegment } from "./selectors"
import { getChips } from "./selectors/chips"
import { getDatesAdjusted, getFirstDate, getPerformanceFetchStartDate, getFetchStartDate } from "./selectors/dates"
import { getChipSecuritiesIds, getClassificationsTiers, getEditingFilter, getFilterOns } from "./selectors/filters"
import withUrlParams from "hocs/withUrlParams"

export default withUrlParams(createModule({
  view,
  externalProps: (state, props) => ({
    account: get(state, "global.selected.account"),
    classificationsTiers: getClassificationsTiers(state, props),
    startDate: getFetchStartDate(state, props),
    performanceStartDate: getPerformanceFetchStartDate(state, props),
    endDate: getEndDate(state, props),
    modelId: getSelectedModelId(state, props),
    portfolioId: get(state, "global.selected.portfolio"),
    securitiesIds: getChipSecuritiesIds(state, props),
    isExpanded: getIsExpanded(state, props),
  }),
  required: ["account", "modelId"],
  data: [{
    resource: "classifications",
    as: null,
    url: () => ({
      id: "classifications",
      query: classificationsQuery,
    }),
  }, {
    resource: "classificationsValues",
    as: null,
    url: ({ classificationsTiers, startDate }) => {
      if (size(classificationsTiers) === 0 || !startDate) {
        return null
      }

      // We don't know the depth of each classification, generate each classification sub query according to the tier depth
      return {
        id: `classificationsValues-${startDate}-${JSON.stringify(classificationsTiers)}`,
        query: `query {
          ${generateClassificationQuery(classificationsTiers)}
        }`,
      }
    },
  }, {
    resource: "portfolioFilters",
    as: null,
    passthrough: true,
    url: ({ account, modelId, portfolioId, performanceStartDate, endDate, isExpanded }) => `/api/${account}/m/${modelId}/r/portfolio/${portfolioId}/filters?startDate=${performanceStartDate}&endDate=${endDate}&expand=${isExpanded}`,
  }, {
    resource: "portfolioSecuritiesDescriptors",
    url: ({ account, modelId, securitiesIds, endDate }) => {
      if (size(securitiesIds) === 0) {
        return null
      }

      return {
        url: `/api/${account}/m/${modelId}/securities/descriptors?date=${endDate}`,
        id: `securitiesDescriptors${account}${modelId}${endDate}${JSON.stringify(securitiesIds)}`,
        payload: { ids: securitiesIds },
      }
    },
  }, {
    resource: "hasUnexpandedComposites",
    passthrough: true,
    url: ({ account, portfolioId, modelId, startDate, endDate }) => `/api/${account}/p/${portfolioId}/${modelId}/unexpanded-composites?from=${startDate}&to=${endDate}`,
  },
  ],
  mapStateToProps: (state, props) => ({
    ...get(state, "modules.assetAnalytics"),
    availableRollups: getAvailableRollups(state, props),
    base: getBasePositionSetWithSegment(state, props),
    baseSignature: getSegmentAwareBaseSignature(state, props),
    chips: getChips(state, props),
    currentGrouping: getGrouping(state, props),
    datesAdjusted: getDatesAdjusted(state, props),
    earliestDate: getEarliestDate(state, props),
    editingFilter: getEditingFilter(state, props),
    filterOns: getFilterOns(state, props),
    firstDate: getFirstDate(state, props),
    includeNotHeld: getIncludeNotHeld(state, props),
    isActive: getIsActiveView(state, props),
    isBenchmarkUnfiltered: getIsBenchmarkUnfiltered(state, props),
    isOpPro: getIsOpPro(state, props),
    latestDate: getLatestDate(state, props),
    period: getPeriod(state, props),
    segment: getQuerySegment(state, props),
    showNotHeld: getShowNotHeld(state, props),
    viewType: getViewType(state, props),
    hasUnexpandedComposites: getHasUnexpandedComposites(state, props),
    portfolioName: getPortfolioName(state, props),
    hasUnsupportedContributionFilter: getHasUnsupportedContributionFilter(state, props),
    currency: getCurrency(state, props),
    isAiTermsAccepted: getAiTermsAccepted(state, props),
    isPortfolioFiltersLoading: getIsPortfolioFiltersLoading(state, props),
    isHasUnexpandedCompositesLoading: getIsHasUnexpandedCompositesLoading(state, props),
  }),
  actions: {
    ...Creators,
    addNotification: NotificationsCreators.add,
    setExpansionMode: PortfolioCreators.setExpansionMode,
    setPeriod: PortfolioCreators.setPeriod,
  },
}))
